import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import WarningIcon from '@mui/icons-material/Warning';
import { debounce, TextField, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Popover from '@mui/material/Popover';
import Skeleton from "@mui/material/Skeleton";
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { L } from 'harmony-language';
import React, { useRef } from 'react';
import { OrgQueryKeys } from '../../api/config';
import { useLoadsInTransit } from '../../api/queries/use-loads-in-transit';
import { useOrganizationQuery } from '../../api/queries/use-organization-query';
import { useTractorTrailer } from '../../api/queries/use-tractor-trailer';
import { useUser } from '../../api/queries/use-user';
import { type DeviceLocation, type DeviceLocationParsed, type Driver } from '../../types';
import { localDateTimeDisplay } from '../../utils/date-time-utils';
import { convertSpeedToDisplay } from '../../utils/unit-conversion';
import { useWeights } from '../shared/hooks/use-weights';
import { getGrossWeight } from '../shared/load-table/utils/load-utils';
import {
    buildDupeResourceList,
    findDuplicateResourceIdsForFields,
    findFieldsWithDuplicatesForLocation,
    markDuplicateFieldsForLocations
} from './utils/live-map-list-util';

const DeviceCard = styled(Card)({
    border: '1px solid transparent',
    '&> div': {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        fontSize: '.8rem',
        padding: '.4rem .4rem .6rem .8rem',
        color: '#666',
        justifyContent: 'space-between',

        '&:last-child': {
            paddingBottom: '.4rem'
        },
        '& span': {
            color: '#000'
        },
        '&.selected': {
            borderColor: 'red',
        }
    },
});

const StyledPopover = styled(Popover)({
    pointerEvents: 'none',
    maxWidth: '30rem',
    '& .MuiPopover-paper': {
        padding: '0.5rem',
    }
})

const Timestamp = styled('div')({
    paddingTop: '.3rem',
    fontSize: '.7rem'
})

const StyledWarningIcon = styled(WarningIcon)(({ theme }) => ({
    fontSize: '1.1rem',
    color: theme.palette.error.main,
}));

const DeviceListWarningContainer = styled('div')({
    position: 'absolute',
    right: '0.5rem',
});

interface DeviceListItemProps {
    deviceLocation: DeviceLocationParsed;
    onClick: (deviceLocation: DeviceLocationParsed) => void;
    isSelected: boolean;
    driverName: string;
}

const DeviceListItem = (props: DeviceListItemProps) => {
    const { deviceLocation: dl, onClick, isSelected, driverName } = props;

    const executeScroll = () => scrollRef?.current?.scrollIntoView && scrollRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
    })

    const { tractors, trailers, isLoading: isLoadingEquipment } = useTractorTrailer();
    const { data: loads, isLoading: isLoadingLoads } = useLoadsInTransit();
    const { convertFromGramsDisplay } = useWeights();
    const { user } = useUser();

    const scrollRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    React.useEffect(() => {
        if (isSelected) {
            executeScroll();
        }
    }, [isSelected]);

    const isLoading = isLoadingEquipment || isLoadingLoads;
    const tractor = tractors?.find(t => t.id === dl.tractorId);
    const tractorName = tractor?.userDisplayName;
    const trailer = trailers?.find(t => t.id === dl.trailerId);
    const trailerName = trailer?.userDisplayName;
    const load = loads?.find(l => l.id === dl.orderId);
    const grossWeightObj = getGrossWeight(tractor, trailer, load?.stops, convertFromGramsDisplay);
    const unitSystem = user.contact?.units || user.organization?.units;

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const renderDupeResourceWarning = (deviceLocation: DeviceLocationParsed) => {
        const duplicateIdFields = findFieldsWithDuplicatesForLocation(deviceLocation);
        if (duplicateIdFields.length < 1) return null;
        return (
            <>
                <DeviceListWarningContainer onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose}>
                    <StyledWarningIcon />
                </DeviceListWarningContainer>
                <StyledPopover
                    id='mouse-over-popover'
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                >
                    <Typography align='center' variant='subtitle1'>{L.duplicateResourcesFound()}</Typography>
                    <Typography align='center' variant='body2'
                                color='error'>{buildDupeResourceList(duplicateIdFields)}</Typography>
                    <Divider sx={{ margin: '0.5rem'}} />
                    <Typography align='center' variant='subtitle2'>{L.duplicateResourcesFoundMessage()}</Typography>
                </StyledPopover>
            </>
        );
    };

    const renderContent = () => {
        return (
            <div>
                {renderDupeResourceWarning(dl)}
                <div>{L.driver()}: <span>{driverName}</span></div>
                {tractorName && <div>{L.tractor()}: <span>{tractorName}</span></div>}
                {trailerName && <div>{L.trailer()}: <span>{trailerName}</span></div>}
                {load && <div>{L.load()}: <span>{load.id}</span></div>}
                {grossWeightObj && (
                    <Tooltip
                        arrow
                        title={
                            <div style={{ whiteSpace: 'pre-line' }}>
                                {grossWeightObj.tooltipText}
                            </div>
                        }
                        placement='right'
                    >
                        <div>{L.grossWeight()}: <span>{`${grossWeightObj.grossWeight}*`}</span></div>
                    </Tooltip>
                )}
                {dl.speed != null && <div>{L.speed()}: <span>{convertSpeedToDisplay(unitSystem)(dl.speed)}</span></div>}
                <Timestamp>{localDateTimeDisplay(dl.timestamp)}</Timestamp>
            </div>
        );
    };

    const renderSkeleton = () => {
        return (
            <>
                <Skeleton width={100}/>
                <Skeleton width={100}/>
                <Skeleton width={100}/>
            </>
        );
    };

    return (
        <ListItem className='live-map-sidebar-item'>
            <DeviceCard ref={scrollRef}>
                <CardContent
                    data-testid={`live-map-sidebar-item-${dl.deviceId}`}
                    onClick={() => {
                        onClick(dl);
                        executeScroll();
                        // scrollToElement(scrollRef);
                    }}>
                    <div>
                        {isLoading ? renderSkeleton() : renderContent()}
                    </div>
                    <ArrowForwardIosIcon sx={{ color: '#aaa' }} />
                </CardContent>
            </DeviceCard>
        </ListItem>
    );
};

export const LiveMapList: React.FC<{ deviceLocations?: DeviceLocation[], onClick: (deviceLocation: DeviceLocation) => void, selectedDevice?: DeviceLocation | null }> = (props) => {
    const { deviceLocations = [], onClick, selectedDevice } = props;
    const dupedFields = findDuplicateResourceIdsForFields(deviceLocations, ['orderId', 'userId', 'tractorId', 'trailerId']);
    const parsedDeviceLocations = markDuplicateFieldsForLocations(deviceLocations, dupedFields);
    const { data: drivers, isLoading: isLoadingDrivers } = useOrganizationQuery<Driver[]>(OrgQueryKeys.drivers);

    const [searchValue, setSearchValue] = React.useState('');

    const resultingDeviceLocations = parsedDeviceLocations?.filter(dl => {
        const driver = drivers?.find(d => d.id === dl.userId);

        return driver?.firstName.toLowerCase().includes(searchValue.toLowerCase())
            || driver?.lastName.toLowerCase().includes(searchValue.toLowerCase());
    });
        
    return (
        <>
            <TextField
                variant='standard'
                label={L.search()}
                onChange={(e) => {
                    debounce(() => setSearchValue(e.target.value), 300)();
                }}
            />
            <div style={{ marginTop: '1rem' }}>
                {resultingDeviceLocations && !isLoadingDrivers ? (
                    <Typography variant='subtitle2'>{L.liveMapTrackedItemsCount(`${parsedDeviceLocations?.length}`)}</Typography>
                ):(
                    <Skeleton/>
                )}
            </div>
            <List>
                {resultingDeviceLocations?.map(dl => {
                    const driver = drivers?.find(d => d.id === dl.userId);
                    const driverName = driver ? `${driver.lastName}, ${driver.firstName}` : '';
                    
                    return (
                        <DeviceListItem key={dl.deviceId}
                            deviceLocation={dl}
                            onClick={onClick}
                            isSelected={dl.deviceId === selectedDevice?.deviceId}
                            driverName={driverName}
                        />
                    )
                })}
            </List>
        </>
    );
};

